import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  TextInput,
  FlatList,
  StyleSheet,
  Pressable,
  Animated,
  Modal,
} from "react-native";

import { capitalizeString } from "../../../utils/functions";
import { CaretDown, Check } from "../../../assets/index";
import FieldLabel from "./FieldLabel";

type InputPatientDetailDropdownProps = {
  name: string;
  value: string[];
  selections: string[];
  multiple?: boolean;
  allowSearch?: boolean;
  onSelect?: ((value: string[]) => void) | null;
  textWidth?: number;
  placeholder?: string;
  alignLeft?: boolean;
  fontSize?: number;
  containerStyle?: any;
  useModal?: boolean;
  tooltipText?: string;
  tooltipAccessibilityLabel?: string;
  tooltipTrackingEventName?: string;
  tooltipTrackingProperties?: Record<string, unknown>;
};

const InputPatientDetailDropdown: FC<InputPatientDetailDropdownProps> = ({
  name,
  value,
  selections,
  multiple = false,
  allowSearch = false,
  onSelect = null,
  textWidth = 120,
  placeholder = "Select",
  alignLeft = false,
  fontSize = 16,
  containerStyle = null,
  useModal = false,
  tooltipText,
  tooltipAccessibilityLabel,
  tooltipTrackingEventName,
  tooltipTrackingProperties,
}) => {
  const [search, setSearch] = useState("");
  const [clicked, setClicked] = useState(false);
  const [data, setData] = useState<string[]>(selections);
  const [selectedData, setSelectedData] = useState<string[]>(value);
  const searchRef = useRef<TextInput>(null);
  const [rotateAnim] = useState(new Animated.Value(0));
  const [clickedOutside, setClickedOutside] = useState(false);
  const dropdownContainerRef = useRef(null);

  useEffect(() => {
    setData(selections);
    setSelectedData(value);
  }, [selections]);

  const toggleCaret = () => {
    setClicked(!clicked);
    const rotateTo = clicked ? 0 : 1;
    Animated.timing(rotateAnim, {
      toValue: rotateTo,
      duration: 300,
      useNativeDriver: true,
    }).start();
  };

  const onSearch = (searchValue: string) => {
    if (searchValue.length < search.length) {
      let tempSelections = selections;
      tempSelections = tempSelections.filter((item) => {
        return item.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
      });
      setData(tempSelections);
    } else if (searchValue !== "") {
      let tempSelections = data.filter((item) => {
        return item.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
      });
      setData(tempSelections);
    } else {
      setData(selections);
    }
  };

  const handleWindowClick = useCallback(
    (event: { target: any }) => {
      if (
        dropdownContainerRef.current &&
        // @ts-ignore
        !dropdownContainerRef.current.contains(event.target)
      ) {
        setClickedOutside(true);
      }
    },
    [dropdownContainerRef],
  );

  useEffect(() => {
    if (useModal || typeof window === "undefined") return;
    window.addEventListener("click", handleWindowClick);
    return () => {
      window.removeEventListener("click", handleWindowClick);
    };
  }, [handleWindowClick, useModal]);

  useEffect(() => {
    if (clickedOutside) {
      toggleCaret();
      setClickedOutside(false);
    }
  }, [clickedOutside]);

  return (
    <View
      style={[
        styles.container,
        alignLeft && styles.alignLeftContainer,
        clicked && styles.containerActive,
        containerStyle,
      ]}
    >
      <FieldLabel
        text={name}
        width={textWidth}
        tooltipText={tooltipText}
        tooltipAccessibilityLabel={tooltipAccessibilityLabel}
        tooltipTrackingEventName={tooltipTrackingEventName}
        tooltipTrackingProperties={tooltipTrackingProperties}
        labelStyle={{ fontSize, lineHeight: fontSize * 1.25 }}
      />
      <View style={{ position: "relative", flex: 1 }}>
        <Pressable
          style={[
            styles.placeHolder,
            alignLeft && styles.alignLeftPlaceholder,
            clicked && styles.placeHolderClicked,
          ]}
          onPress={toggleCaret}
        >
          <Text
            style={[
              styles.placeHolderText,
              { fontSize, lineHeight: fontSize * 1.4 },
              selectedData.length === 0 && { color: "#C4C6CD" },
            ]}
          >
            {selectedData.length === 0
              ? placeholder
              : selectedData.map((item) => capitalizeString(item) + "\n")}
          </Text>
          <Animated.View
            style={{
              transform: [
                {
                  rotate: rotateAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: ["0deg", "180deg"],
                  }),
                },
              ],
            }}
          >
            <CaretDown />
          </Animated.View>
        </Pressable>
        {clicked && !useModal && (
          <Animated.View
            ref={dropdownContainerRef}
            style={[styles.dropdownContainer, { opacity: rotateAnim }]}
          >
            {allowSearch && (
              <View style={{ marginBottom: 4 }}>
                <TextInput
                  placeholder="Search ..."
                  value={search}
                  ref={searchRef}
                  onChangeText={(txt) => {
                    setSearch(txt);
                    onSearch(txt);
                  }}
                  style={[
                    styles.placeHolderText,
                    { fontSize, lineHeight: fontSize * 1.4 },
                  ]}
                />
              </View>
            )}
            <FlatList
              data={data}
              renderItem={({ item, index }) => {
                if (selectedData.includes(item))
                  return (
                    <Pressable
                      key={index}
                      style={[styles.selectedDataItem]}
                      onPress={() => {
                        let temp;
                        if (multiple) {
                          temp = selectedData.filter((data) => data !== item);
                        } else {
                          temp = [item];
                        }
                        setSelectedData(temp);

                        if (onSelect) onSelect(temp);
                        if (!multiple) toggleCaret();
                        onSearch("");
                        setSearch("");
                      }}
                    >
                      <Text
                        style={[
                          styles.placeHolderText,
                          { fontSize, lineHeight: fontSize * 1.4 },
                        ]}
                      >
                        {capitalizeString(item)}
                      </Text>
                      <View style={styles.checkIconContainer}>
                        <Check />
                      </View>
                    </Pressable>
                  );
                return (
                  <Pressable
                    key={index}
                    onPress={() => {
                      let temp;
                      if (multiple) {
                        temp = [...selectedData, item];
                      } else {
                        temp = [item];
                      }
                      setSelectedData(temp);
                      if (onSelect) onSelect(temp);
                      if (!multiple) toggleCaret();
                      onSearch("");
                      setSearch("");
                    }}
                  >
                    <Text
                      style={[
                        styles.placeHolderText,
                        { fontSize, lineHeight: fontSize * 1.4 },
                      ]}
                    >
                      {capitalizeString(item)}
                    </Text>
                  </Pressable>
                );
              }}
            />
          </Animated.View>
        )}
        {clicked && useModal ? (
          <Modal
            transparent
            animationType="fade"
            visible={clicked}
            onRequestClose={toggleCaret}
          >
            <View style={styles.modalRoot}>
              <Pressable style={styles.modalBackdrop} onPress={toggleCaret} />
              <View style={styles.modalCard}>
                {allowSearch ? (
                  <View style={{ marginBottom: 6 }}>
                    <TextInput
                      placeholder="Search ..."
                      value={search}
                      ref={searchRef}
                      onChangeText={(txt) => {
                        setSearch(txt);
                        onSearch(txt);
                      }}
                      style={[
                        styles.placeHolderText,
                        styles.modalSearchInput,
                        { fontSize, lineHeight: fontSize * 1.4 },
                      ]}
                    />
                  </View>
                ) : null}
                <FlatList
                  data={data}
                  keyboardShouldPersistTaps="handled"
                  renderItem={({ item, index }) => {
                    if (selectedData.includes(item))
                      return (
                        <Pressable
                          key={index}
                          style={[styles.modalItem, styles.selectedDataItem]}
                          onPress={() => {
                            let temp;
                            if (multiple) {
                              temp = selectedData.filter(
                                (data) => data !== item,
                              );
                            } else {
                              temp = [item];
                            }
                            setSelectedData(temp);
                            if (onSelect) onSelect(temp);
                            if (!multiple) toggleCaret();
                            onSearch("");
                            setSearch("");
                          }}
                        >
                          <Text
                            style={[
                              styles.placeHolderText,
                              { fontSize, lineHeight: fontSize * 1.4 },
                            ]}
                          >
                            {capitalizeString(item)}
                          </Text>
                          <View style={styles.checkIconContainer}>
                            <Check />
                          </View>
                        </Pressable>
                      );
                    return (
                      <Pressable
                        key={index}
                        style={styles.modalItem}
                        onPress={() => {
                          let temp;
                          if (multiple) {
                            temp = [...selectedData, item];
                          } else {
                            temp = [item];
                          }
                          setSelectedData(temp);
                          if (onSelect) onSelect(temp);
                          if (!multiple) toggleCaret();
                          onSearch("");
                          setSearch("");
                        }}
                      >
                        <Text
                          style={[
                            styles.placeHolderText,
                            { fontSize, lineHeight: fontSize * 1.4 },
                          ]}
                        >
                          {capitalizeString(item)}
                        </Text>
                      </Pressable>
                    );
                  }}
                />
              </View>
            </View>
          </Modal>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    position: "relative",
    zIndex: 1,
  },
  containerActive: {
    zIndex: 1000,
  },
  alignLeftContainer: {
    justifyContent: "flex-start",
  },
  name: {
    fontFamily: "Urbanist500",
    fontWeight: "500",
    fontSize: 16,
    color: "#282B33",
  },
  placeHolder: {
    width: "100%",
    maxWidth: 260,
    fontFamily: "Urbanist500",
    fontWeight: "500",
    borderRadius: 10,
    borderColor: "#C4C6CD",
    borderWidth: 1,
    paddingHorizontal: 12,
    fontSize: 16,
    color: "#000000",
    marginLeft: "auto",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#fff",
  },
  alignLeftPlaceholder: {
    marginLeft: 0,
    alignSelf: "flex-start",
  },
  placeHolderText: {
    color: "#000",
    fontFamily: "Urbanist500",
    fontWeight: "500",
    fontSize: 16,
    lineHeight: 16 * 1.4,
    letterSpacing: 0.032,
    width: "100%",
    paddingVertical: 4,
    paddingHorizontal: 2,
    userSelect: "none",
  },
  placeHolderClicked: {
    borderWidth: 1,
    borderColor: "#085ccc",
  },
  dropdownContainer: {
    width: "100%",
    maxWidth: 260,
    height: "auto",
    maxHeight: 200,
    backgroundColor: "#fff",
    borderRadius: 6,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 6,
    },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    padding: 4,
    position: "absolute",
    top: 36,
    zIndex: 1001,
    elevation: 10,
  },
  checkIconContainer: {
    position: "absolute",
    right: 8,
  },
  selectedDataItem: {
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: 4,
    backgroundColor: "#F8F8F8",
  },
  modalRoot: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  modalBackdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(11, 14, 30, 0.32)",
  },
  modalCard: {
    width: "100%",
    maxWidth: 520,
    maxHeight: "70%",
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 12,
    paddingHorizontal: 16,
    zIndex: 1,
  },
  modalSearchInput: {
    borderWidth: 1,
    borderColor: "#C4C6CD",
    borderRadius: 8,
    paddingHorizontal: 10,
  },
  modalItem: {
    justifyContent: "center",
    minHeight: 44,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
});

export default InputPatientDetailDropdown;
